import React, { useEffect } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  useTheme,
  Box
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../theme';
import { useSettings } from '../contexts/SettingsContext';
import { useSleeperConnection } from '../hooks/useSleeperConnection';

const LeagueSelector = ({ isCollapsed = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { selectedLeagueId, handleSelectedLeagueChange } = useSettings();
  const { leagues } = useSleeperConnection();

  // Auto-select first league if none is selected and we have leagues
  useEffect(() => {
    if (!selectedLeagueId && leagues.length > 0) {
      handleSelectedLeagueChange(leagues[0].league_id);
    }
  }, [leagues, selectedLeagueId, handleSelectedLeagueChange]);

  const handleChange = (event) => {
    const selectedLeagueId = event.target.value;
    handleSelectedLeagueChange(selectedLeagueId);
    
    // Find the selected league to get its ID for navigation
    const selectedLeague = leagues.find(league => league.league_id === selectedLeagueId);
    if (selectedLeague && selectedLeague.id) {
      navigate(`/leagues/${selectedLeague.id}`);
    }
  };

  if (isCollapsed) {
    return null; // Don't show dropdown when sidebar is collapsed
  }

  if (leagues.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ color: colors.grey[500] }}>
          No leagues found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="caption"
        sx={{ 
          color: colors.grey[400], 
          mb: 1,
          display: 'block',
          fontSize: '0.75rem'
        }}
      >
        Select League
      </Typography>
      <FormControl fullWidth size="small">
        <Select
          value={selectedLeagueId}
          onChange={handleChange}
          displayEmpty
          sx={{
            color: colors.grey[100],
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.grey[600],
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.blueAccent[500],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.blueAccent[500],
            },
            '& .MuiSelect-icon': {
              color: colors.grey[300],
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: colors.primary[400],
                '& .MuiMenuItem-root': {
                  color: colors.grey[100],
                  '&:hover': {
                    backgroundColor: colors.primary[600],
                  },
                  '&.Mui-selected': {
                    backgroundColor: colors.blueAccent[800],
                    '&:hover': {
                      backgroundColor: colors.blueAccent[700],
                    },
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="" disabled>
            <Typography variant="body2" sx={{ color: colors.grey[500] }}>
              Select a league...
            </Typography>
          </MenuItem>
          {leagues.map((league) => (
            <MenuItem key={league.league_id} value={league.league_id}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {league.name || `League ${league.league_id.slice(0, 8)}`}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default LeagueSelector;
